import React, { FC, PropsWithChildren, useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import { twJoin, twMerge } from 'tailwind-merge';

export interface PingProps extends PropsWithChildren<{}> {
  /**
   * The position of the ping
   */
  position?: 'left' | 'right';
  /**
   * Whether to enable the ping
   */
  enable?: boolean;
  /**
   * The class name to apply to the wrapper container
   */
  className?: string;
  /**
   * The tooltip to display when the user hovers over the ping
   */
  tooltip: string;
  testId?: string;
}

export const Ping: React.FC<PingProps> = ({
  position = 'right',
  className,
  children,
  tooltip,
  testId = 'ping',
  enable = true,
}) => {
  return (
    <div className={twMerge('relative inline-block', className)}>
      {enable && (
        <div
          className={twJoin(
            'absolute h-0 w-0',
            position === 'right' ? 'right-0' : 'left-0',
          )}
        >
          <div
            aria-label={tooltip}
            className="h-4 w-4 -translate-x-1/2 -translate-y-1/2"
            data-testid={testId}
            role="img"
            title={tooltip}
          >
            <div className="absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75 motion-safe:animate-ping" />
            <div className="absolute inline-flex h-full w-full rounded-full bg-orange-600" />
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

const featureNames = [] as const;

type FeatureName = (typeof featureNames)[number];

interface NewFeaturePingProps {
  featureName: FeatureName;
}

const FEATURE_PREFIX = 'dbt-cloud.explorer.feature.';

export const clearOldFeatures = (localStorage: Storage) => {
  Object.keys(localStorage).forEach((key) => {
    if (key !== null && key.startsWith(FEATURE_PREFIX)) {
      const featureName = key.slice(FEATURE_PREFIX.length);
      if (!featureNames.includes(featureName as FeatureName)) {
        localStorage.removeItem(key);
      }
    }
  });
};

export const useNewFeature = ({ featureName }: NewFeaturePingProps) => {
  const [hasSeenFeature, setHasSeen] = useLocalStorage(
    `${FEATURE_PREFIX}${featureName}`,
    false,
  );

  return useMemo(() => {
    const NewFeature: FC<Omit<PingProps, 'tooltip' | 'enable'>> = (props) => (
      <Ping
        {...props}
        tooltip="New feature"
        enable={!hasSeenFeature}
        testId={featureName}
      />
    );
    return {
      hidePing: () => setHasSeen(true),
      NewFeature,
    };
  }, [featureName, hasSeenFeature, setHasSeen]);
};
